import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from 'react-daisyui';
import TopNavbar from './TopNavbar';
import Sidebar from './Sidebar';

const API_BASE_URL = 'https://berachain-v2-blacknodes-el-api.blacknodes.net/api/v2';

const InfoRow = ({ label, value, icon }) => (
  <div className="flex items-center py-2 border-b border-gray-700">
    <div className="w-1/3 flex items-center">
      {icon && <span className="mr-2">{icon}</span>}
      <span className="text-gray-400">{label}</span>
    </div>
    <div className="w-2/3 break-all">{value}</div>
  </div>
);

const SearchResultPage = () => {
  const { txHash } = useParams();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTransactionData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_BASE_URL}/transactions/${txHash}`);
        setTransaction(response.data);
      } catch (err) {
        setError('An error occurred while fetching the transaction data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactionData();
  }, [txHash]);

  const renderTransactionDetails = () => {
    if (!transaction) return <div>No transaction data available</div>;

    return (
      <Card className="bg-gray-900 border border-gray-800 mb-4 shadow-[0px_20px_207px_10px_rgba(38,_126,_218,_0.93)]">
        <Card.Body>
          <Card.Title tag="h2" className="text-2xl mb-4">Transaction Details</Card.Title>
          <InfoRow
            label="Transaction Hash"
            value={
              <span>
                {transaction.hash}{' '}
                <button className="ml-2 text-blue-400">
                  <i className="fas fa-copy"></i>
                </button>
              </span>
            }
            icon={<i className="fas fa-info-circle text-gray-400"></i>}
          />
          <InfoRow
            label="Result"
            value={
              <span className={transaction.status === 'ok' ? "text-green-500" : "text-red-500"}>
                {transaction.status === 'ok' ? 'Success' : 'Failed'}
              </span>
            }
            icon={<i className="fas fa-info-circle text-gray-400"></i>}
          />
          <InfoRow
            label="Status"
            value={
              <span>
                <span className="bg-green-500 text-white px-2 py-1 rounded mr-2">Confirmed</span>
                <span className="text-gray-400">Confirmed by {transaction.confirmations}</span>
              </span>
            }
            icon={<i className="fas fa-info-circle text-gray-400"></i>}
          />
          <InfoRow
            label="Block"
            value={transaction.block}
            icon={<i className="fas fa-cube text-gray-400"></i>}
          />
          <InfoRow
            label="Timestamp"
            value={`${new Date(transaction.timestamp).toLocaleString()} | Confirmed within <= ${transaction.confirmation_duration[1]} seconds`}
            icon={<i className="fas fa-clock text-gray-400"></i>}
          />
          <InfoRow
            label="From"
            value={
              <span>
                {transaction.from.hash}{' '}
                <button className="ml-2 text-blue-400">
                  <i className="fas fa-copy"></i>
                </button>
              </span>
            }
            icon={<i className="fas fa-user text-gray-400"></i>}
          />
          <InfoRow
            label="Interacted With (To)"
            value={
              <span>
                {transaction.to ? transaction.to.hash : 'Contract Creation'}{' '}
                <button className="ml-2 text-blue-400">
                  <i className="fas fa-copy"></i>
                </button>
              </span>
            }
            icon={<i className="fas fa-user text-gray-400"></i>}
          />
          {transaction.token_transfers && transaction.token_transfers.length > 0 && (
            <InfoRow
              label="Tokens Transferred"
              value={
                <div>
                  {transaction.token_transfers.map((transfer, index) => (
                    <div key={index} className="mb-2">
                      <p><strong>From:</strong> {transfer.from.hash}</p>
                      <p><strong>To:</strong> {transfer.to.hash}</p>
                      <p><strong>For:</strong> {(parseInt(transfer.total.value) / Math.pow(10, parseInt(transfer.token.decimals))).toFixed(18)} {transfer.token.symbol}</p>
                    </div>
                  ))}
                </div>
              }
              icon={<i className="fas fa-exchange-alt text-gray-400"></i>}
            />
          )}
          <InfoRow
            label="Value"
            value={`${transaction.value} BERA`}
            icon={<i className="fas fa-coins text-gray-400"></i>}
          />
          <InfoRow
            label="Transaction Fee"
            value={`${(parseInt(transaction.fee.value) / 1e18).toFixed(18)} BERA`}
            icon={<i className="fas fa-gas-pump text-gray-400"></i>}
          />
          <InfoRow
            label="Gas Price"
            value={`${(parseInt(transaction.gas_price) / 1e9).toFixed(9)} Gwei`}
            icon={<i className="fas fa-gas-pump text-gray-400"></i>}
          />
          <InfoRow
            label="Transaction Type"
            value={`${transaction.type} (EIP-1559)`}
            icon={<i className="fas fa-info-circle text-gray-400"></i>}
          />
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white flex">
      <Sidebar />
      <div className="flex-1 p-4 pl-20">
        <TopNavbar />
        <div className="mb-4">
          <h1 className="text-3xl font-bold mb-8">Transaction Details</h1>
          {loading ? (
            <div className="text-center mt-8">Loading...</div>
          ) : error ? (
            <div className="text-center mt-8 text-red-500">{error}</div>
          ) : (
            renderTransactionDetails()
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResultPage;